
import { useMemo } from "react";
import { SWRConfiguration, SWRResponse } from "swr";


import useSwrClient from "./useSwrClient";
import { IReturn } from '@/api/dtos';


export interface IGetConfiguration<T, D, E, A> {
    getData?: (response?: SWRResponse<T, T>) => D | undefined;
    getError?: (response?: SWRResponse<T, T>) => E | undefined;
    getAdditionalState?: (response?: SWRResponse<T, T>) => A | undefined;
}

export const useGet = <T, D, E, A>(fn?: () => IReturn<T> | string, getConfiguration?: IGetConfiguration<T, D, E, A>, swrConfiguration?: SWRConfiguration | undefined) => {
    const { get } = useSwrClient();
    const response = get<T>(fn, swrConfiguration);

    const { getData, getError, getAdditionalState } = getConfiguration ?? {};

    const result = useMemo(() => {
        const d = getData?.(response);
        const e = getError?.(response);
        const a = getAdditionalState?.(response);

        const s = {
            data: d,
            error: e,
            loading: response.isValidating && !d && !e,
            validating: response.isValidating,
            mutate: response.mutate,
            refresh: async () => {
                const newResponse = await response.mutate(response.data, { revalidate: true });

                return getData?.(newResponse as any);
            }
        }

        return {
            ...s,
            ...a
        } as typeof s & A

    }, [getData, getError, response.data, response.error, response.isLoading, response.isValidating, response.mutate])

    return result;
}

export default useGet;